
import React, { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useTranslation } from 'react-i18next';

interface DateOfBirthSelectorProps {
  value?: string; // ISO date string (YYYY-MM-DD)
  onChange: (date: string | null) => void;
  disabled?: boolean;
  error?: string;
}

export const DateOfBirthSelector: React.FC<DateOfBirthSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  error
}) => {
  const { t, i18n } = useTranslation('dashboard');
  const [year, setYear] = useState<string>('');
  const [month, setMonth] = useState<string>('');
  const [day, setDay] = useState<string>('');

  // Parse initial value
  useEffect(() => {
    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        setYear(date.getFullYear().toString());
        setMonth((date.getMonth() + 1).toString().padStart(2, '0'));
        setDay(date.getDate().toString().padStart(2, '0'));
      }
    } else {
      setYear('');
      setMonth('');
      setDay('');
    }
  }, [value]);

  // Generate years (1900 to current year)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1899 }, (_, i) => (currentYear - i).toString());

  // Month names
  const months = [
    { value: '01', label: i18n.language === 'zh' ? '1月' : 'January' },
    { value: '02', label: i18n.language === 'zh' ? '2月' : 'February' },
    { value: '03', label: i18n.language === 'zh' ? '3月' : 'March' },
    { value: '04', label: i18n.language === 'zh' ? '4月' : 'April' },
    { value: '05', label: i18n.language === 'zh' ? '5月' : 'May' },
    { value: '06', label: i18n.language === 'zh' ? '6月' : 'June' },
    { value: '07', label: i18n.language === 'zh' ? '7月' : 'July' },
    { value: '08', label: i18n.language === 'zh' ? '8月' : 'August' },
    { value: '09', label: i18n.language === 'zh' ? '9月' : 'September' },
    { value: '10', label: i18n.language === 'zh' ? '10月' : 'October' },
    { value: '11', label: i18n.language === 'zh' ? '11月' : 'November' },
    { value: '12', label: i18n.language === 'zh' ? '12月' : 'December' },
  ];

  // Calculate days in month
  const getDaysInMonth = (year: string, month: string) => {
    if (!year || !month) return 31;
    const daysInMonth = new Date(parseInt(year), parseInt(month), 0).getDate();
    return daysInMonth;
  };

  const daysInSelectedMonth = getDaysInMonth(year, month);
  const days = Array.from({ length: daysInSelectedMonth }, (_, i) => (i + 1).toString().padStart(2, '0'));

  // Handle value changes
  const handleChange = (newYear: string, newMonth: string, newDay: string) => {
    if (newYear && newMonth && newDay) {
      // Validate the date
      const date = new Date(parseInt(newYear), parseInt(newMonth) - 1, parseInt(newDay));
      if (date.getFullYear() === parseInt(newYear) && 
          date.getMonth() === parseInt(newMonth) - 1 && 
          date.getDate() === parseInt(newDay)) {
        onChange(`${newYear}-${newMonth}-${newDay}`);
      } else {
        onChange(null);
      }
    } else {
      onChange(null);
    }
  };

  const handleYearChange = (newYear: string) => {
    setYear(newYear);
    // Reset day if it's invalid for the new year/month combination
    const newDaysInMonth = getDaysInMonth(newYear, month);
    const newDay = parseInt(day) > newDaysInMonth ? newDaysInMonth.toString().padStart(2, '0') : day;
    setDay(newDay);
    handleChange(newYear, month, newDay);
  };

  const handleMonthChange = (newMonth: string) => {
    setMonth(newMonth);
    // Reset day if it's invalid for the new month
    const newDaysInMonth = getDaysInMonth(year, newMonth);
    const newDay = parseInt(day) > newDaysInMonth ? newDaysInMonth.toString().padStart(2, '0') : day;
    setDay(newDay);
    handleChange(year, newMonth, newDay);
  };

  const handleDayChange = (newDay: string) => {
    setDay(newDay);
    handleChange(year, month, newDay);
  };

  return (
    <div className="space-y-2">
      <Label>{t('personalInfo.dateOfBirth')}</Label>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Select value={year} onValueChange={handleYearChange} disabled={disabled}>
            <SelectTrigger>
              <SelectValue placeholder={i18n.language === 'zh' ? '年' : 'Year'} />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={y}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Select value={month} onValueChange={handleMonthChange} disabled={disabled}>
            <SelectTrigger>
              <SelectValue placeholder={i18n.language === 'zh' ? '月' : 'Month'} />
            </SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Select value={day} onValueChange={handleDayChange} disabled={disabled}>
            <SelectTrigger>
              <SelectValue placeholder={i18n.language === 'zh' ? '日' : 'Day'} />
            </SelectTrigger>
            <SelectContent>
              {days.map((d) => (
                <SelectItem key={d} value={d}>
                  {d}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
      
      <p className="text-sm text-muted-foreground">
        {t('personalInfo.dateOfBirthDescription')}
      </p>
    </div>
  );
};
